import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { runsApi } from "@/api/runs";
import { JsonViewer } from "@/components/json-viewer";
import { StatusBadge } from "@/components/status-badge";
import { formatAbsolute } from "@/lib/format";
import type { EventSummary } from "@/api/types";

interface Props {
  event: EventSummary;
}

/** Right-pane detail for the selected stream event: payload + triggered runs. */
export function StreamEventDetail({ event }: Props) {
  const runsQ = useQuery({
    queryKey: ["event-runs", event.id],
    queryFn: () => runsApi.list({ limit: 200 }),
  });

  const runs = (runsQ.data?.data ?? []).filter((r) => r.event_id === event.id);

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-1 font-mono text-[15px] font-semibold tracking-tight">{event.name}</div>
        <div className="font-mono text-xs text-muted-foreground">{formatAbsolute(event.received_at)}</div>
      </div>

      <div>
        <div className="mb-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Payload</div>
        <JsonViewer data={event.data} />
      </div>

      <div>
        <div className="mb-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          Triggered runs
        </div>
        {runsQ.isLoading ? (
          <div className="text-xs text-muted-foreground">Checking for triggered runs…</div>
        ) : runs.length === 0 ? (
          <div className="rounded-md border border-dashed px-3 py-3 text-center text-xs text-muted-foreground">
            This event didn't trigger any runs.
          </div>
        ) : (
          <div className="divide-y rounded-md border">
            {runs.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                <StatusBadge status={r.status} />
                <Link
                  to="/runs/$id"
                  params={{ id: r.id }}
                  className="min-w-0 flex-1 truncate text-right font-mono text-[13px] text-foreground hover:text-brand"
                >
                  {r.function_slug}
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
